<script setup lang="ts">
import type { WikiVehicle } from "~~/types/gamedata";

const props = defineProps({
  raw: {
    type: Object as () => WikiVehicle,
    required: true,
  },
});

interface LinkButton {
  key: string;
  label: string;
  icon: string;
  url: string;
}

/** Mehrfach-Felder (`presentation_urls`/`qa_urls`) nummerieren sich nur, wenn tatsächlich mehr als ein Eintrag vorhanden ist. */
function expand(label: string, icon: string, urls: string[] | null | undefined): LinkButton[] {
  const list = (urls ?? []).filter((url): url is string => !!url);
  return list.map((url, index) => ({
    key: `${label}-${index}`,
    label: list.length > 1 ? `${label} ${index + 1}` : label,
    icon,
    url,
  }));
}

const links = computed<LinkButton[]>(() => {
  const curated = props.raw.curated_data;
  if (!curated) return [];

  return [
    ...(curated.brochure_url ? [{ key: 'brochure', label: 'Broschüre', icon: 'i-lucide-file-text', url: curated.brochure_url }] : []),
    ...expand('Präsentation', 'i-lucide-presentation', curated.presentation_urls),
    ...(curated.whitleys_guide_url ? [{ key: 'whitleys', label: "Whitley's Guide", icon: 'i-lucide-compass', url: curated.whitleys_guide_url }] : []),
    ...(curated.galactapedia_url ? [{ key: 'galactapedia', label: 'Galactapedia', icon: 'i-lucide-library', url: curated.galactapedia_url }] : []),
    ...expand('Q&A', 'i-lucide-circle-help', curated.qa_urls),
  ];
});
</script>

<template>
  <UCard v-if="links.length" variant="outline" class="border border-default bg-bunker-950 ring-0" :ui="{ body: 'py-2 sm:py-2 sm:px-0 px-0' }">
    <div class="flex items-center space-x-2 border-b border-default pt-0 pb-2">
      <UIcon name="i-lucide-link" class="size-5 ml-3" />
      <span class="text-sm text-primary font-medium">Links</span>
    </div>
    <div class="flex flex-wrap gap-2 px-4 py-3">
      <UButton
        v-for="link in links"
        :key="link.key"
        :to="link.url"
        target="_blank"
        rel="noopener noreferrer"
        :label="link.label"
        :icon="link.icon"
        color="neutral"
        variant="outline"
        size="sm"
      />
    </div>
  </UCard>
</template>
